// Learning Towers — visual prototype only, no chat wiring yet.
// Goal: let Louise judge the look & vibe before committing to the full build.
//
// Design notes:
//   • Soft pastel palette pulled from existing Poodle Talk brand
//     (cream #FDFBF7, butter #FFF7D6, pink #FCEAEA + extensions)
//   • 2.5D feel via a left-side shadow on each tower (no 3D libs)
//   • 2 rows × 3 towers — back row slightly smaller for depth
//   • Each tower has a UNIQUE silhouette + roof so they're distinguishable
//     even before you read the label (helpful for SEND / pre-readers)
//   • Gentle bob animation, hover lift + glow, label tooltip
import { useState } from "react";

// ---------- SVG building blocks ----------

const TowerShadow = () => (
  <ellipse cx="60" cy="200" rx="50" ry="8" fill="#000" opacity="0.12" />
);

// Tiny windows row helper
const WindowRow = ({ y, count = 3, fill = "#FDFBF7" }) => (
  <g>
    {Array.from({ length: count }).map((_, i) => (
      <rect
        key={i}
        x={32 + i * 16}
        y={y}
        width="8"
        height="10"
        rx="1.5"
        fill={fill}
        opacity="0.85"
      />
    ))}
  </g>
);

// ---------- Individual towers ----------

// 📚 English — classical stone tower with a big open book on top
const EnglishTower = () => (
  <svg viewBox="0 0 120 210" className="tower-svg">
    <TowerShadow />
    {/* Body */}
    <rect x="20" y="60" width="80" height="135" rx="6" fill="#FFE89A" />
    {/* Left shadow side for 2.5D */}
    <rect x="20" y="60" width="14" height="135" rx="6" fill="#E8C66A" opacity="0.55" />
    {/* Roof — stepped */}
    <rect x="14" y="50" width="92" height="14" rx="3" fill="#D9A93D" />
    <rect x="22" y="38" width="76" height="14" rx="3" fill="#E8B85A" />
    {/* Door */}
    <path d="M52 195 V165 a8 8 0 0 1 16 0 V195 Z" fill="#9C7427" />
    <circle cx="64" cy="180" r="1.2" fill="#FFF7D6" />
    {/* Windows */}
    <WindowRow y={80} fill="#FFF7D6" />
    <WindowRow y={108} fill="#FFF7D6" />
    <WindowRow y={136} fill="#FFF7D6" />
    {/* Book on top */}
    <g transform="translate(60 18)">
      <rect x="-22" y="-8" width="44" height="20" rx="2" fill="#7AB099" />
      <rect x="-22" y="-8" width="44" height="3" fill="#5A8C77" />
      <line x1="0" y1="-8" x2="0" y2="12" stroke="#FDFBF7" strokeWidth="1.5" />
      <rect x="-19" y="-4" width="16" height="2" fill="#FDFBF7" opacity="0.7" />
      <rect x="3" y="-4" width="16" height="2" fill="#FDFBF7" opacity="0.7" />
    </g>
  </svg>
);

// 🔢 Maths — stepped pyramid blocks with a "+" sign sign
const MathsTower = () => (
  <svg viewBox="0 0 120 210" className="tower-svg">
    <TowerShadow />
    {/* 3 stacked blocks getting smaller */}
    <rect x="14" y="150" width="92" height="48" rx="6" fill="#C9E8D6" />
    <rect x="14" y="150" width="14" height="48" rx="6" fill="#7CB99A" opacity="0.45" />

    <rect x="22" y="105" width="76" height="48" rx="6" fill="#B8DEC6" />
    <rect x="22" y="105" width="13" height="48" rx="6" fill="#7CB99A" opacity="0.45" />

    <rect x="32" y="60" width="56" height="48" rx="6" fill="#A9D5B6" />
    <rect x="32" y="60" width="11" height="48" rx="6" fill="#6BA88A" opacity="0.5" />

    {/* Numbers floating */}
    <text x="60" y="86" textAnchor="middle" fontSize="20" fontWeight="700" fill="#3F6B55">+</text>
    <text x="40" y="135" textAnchor="middle" fontSize="14" fontWeight="700" fill="#3F6B55">7</text>
    <text x="80" y="135" textAnchor="middle" fontSize="14" fontWeight="700" fill="#3F6B55">3</text>
    <text x="60" y="180" textAnchor="middle" fontSize="14" fontWeight="700" fill="#3F6B55">π</text>

    {/* Tiny flag on top */}
    <line x1="60" y1="62" x2="60" y2="38" stroke="#7AB099" strokeWidth="2" />
    <path d="M60 38 L80 44 L60 50 Z" fill="#5A8C77" />
  </svg>
);

// 🔬 Science — flask-shaped tower with bubbles
const ScienceTower = () => (
  <svg viewBox="0 0 120 210" className="tower-svg">
    <TowerShadow />
    {/* Wide round bottom */}
    <ellipse cx="60" cy="160" rx="44" ry="38" fill="#C9DFEF" />
    <ellipse cx="60" cy="160" rx="14" ry="38" fill="#7AAFC9" opacity="0.35" />
    {/* Flask neck */}
    <rect x="46" y="80" width="28" height="58" fill="#C9DFEF" />
    <rect x="46" y="80" width="9" height="58" fill="#7AAFC9" opacity="0.4" />
    {/* Lip */}
    <rect x="40" y="74" width="40" height="10" rx="3" fill="#7AAFC9" />
    {/* Liquid inside */}
    <ellipse cx="60" cy="170" rx="36" ry="22" fill="#9CCBE8" opacity="0.7" />
    {/* Bubbles */}
    <circle cx="50" cy="155" r="3" fill="#FDFBF7" opacity="0.9" />
    <circle cx="68" cy="148" r="2" fill="#FDFBF7" opacity="0.9" />
    <circle cx="74" cy="165" r="2.5" fill="#FDFBF7" opacity="0.9" />
    <circle cx="55" cy="175" r="2" fill="#FDFBF7" opacity="0.7" />
    {/* Steam wisps coming out the top */}
    <path d="M50 70 Q48 60 54 54 Q60 50 56 42" stroke="#C9DFEF" strokeWidth="3" fill="none" opacity="0.6" strokeLinecap="round" />
    <path d="M64 70 Q66 62 62 56 Q58 50 64 44" stroke="#C9DFEF" strokeWidth="3" fill="none" opacity="0.6" strokeLinecap="round" />
    <path d="M72 70 Q74 60 70 54" stroke="#C9DFEF" strokeWidth="2.5" fill="none" opacity="0.5" strokeLinecap="round" />
  </svg>
);

// 🎨 Art — striped rainbow tower with paint splash top
const ArtTower = () => (
  <svg viewBox="0 0 120 210" className="tower-svg">
    <TowerShadow />
    <rect x="22" y="55" width="76" height="140" rx="6" fill="#FFD6C2" />
    {/* Stripes — pastel rainbow */}
    <rect x="22" y="65" width="76" height="18" fill="#FCEAEA" />
    <rect x="22" y="100" width="76" height="18" fill="#D8E3C0" />
    <rect x="22" y="135" width="76" height="18" fill="#C9DFEF" />
    <rect x="22" y="170" width="76" height="18" fill="#D9D0EB" />
    {/* Left shadow */}
    <rect x="22" y="55" width="13" height="140" rx="6" fill="#E89070" opacity="0.35" />
    {/* Door */}
    <path d="M52 195 V172 a8 8 0 0 1 16 0 V195 Z" fill="#A14B30" />
    <circle cx="64" cy="184" r="1.2" fill="#FFF7D6" />
    {/* Paint splash on top */}
    <g transform="translate(60 40)">
      <path d="M0 0 q-14 -2 -16 -16 q-2 -10 8 -14 q12 -4 18 6 q12 -8 20 4 q6 10 -2 16 q4 12 -10 12 q-8 6 -18 -8 Z" fill="#F09080" />
      <circle cx="-14" cy="-18" r="3" fill="#FCEAEA" />
      <circle cx="14" cy="-12" r="2.5" fill="#D9D0EB" />
      <circle cx="6" cy="-22" r="2" fill="#FFE89A" />
    </g>
  </svg>
);

// 🌍 Geography — tower with a globe orb on top
const GeographyTower = () => (
  <svg viewBox="0 0 120 210" className="tower-svg">
    <TowerShadow />
    <rect x="24" y="80" width="72" height="118" rx="6" fill="#D8E3C0" />
    <rect x="24" y="80" width="13" height="118" rx="6" fill="#8FA56C" opacity="0.4" />
    {/* Stone band */}
    <rect x="20" y="74" width="80" height="10" rx="2" fill="#A9BC85" />
    {/* Door (round arched) */}
    <path d="M50 198 V170 a10 10 0 0 1 20 0 V198 Z" fill="#5C6B3A" />
    <circle cx="60" cy="184" r="1.5" fill="#FFF7D6" />
    {/* Windows — round porthole-y */}
    <circle cx="42" cy="105" r="5" fill="#FDFBF7" opacity="0.85" />
    <circle cx="78" cy="105" r="5" fill="#FDFBF7" opacity="0.85" />
    <circle cx="60" cy="135" r="6" fill="#FDFBF7" opacity="0.85" />
    {/* Globe orb on top */}
    <g transform="translate(60 50)">
      <circle r="20" fill="#9CCBE8" />
      <path d="M-20 0 a20 8 0 0 0 40 0 a20 8 0 0 0 -40 0 Z" fill="#7AAFC9" opacity="0.5" />
      <ellipse cx="-6" cy="-6" rx="6" ry="4" fill="#A9D5B6" />
      <ellipse cx="8" cy="2" rx="7" ry="5" fill="#A9D5B6" />
      <ellipse cx="-2" cy="10" rx="5" ry="3" fill="#A9D5B6" />
      <line x1="0" y1="-20" x2="0" y2="20" stroke="#5A8C77" strokeWidth="0.5" opacity="0.4" />
    </g>
    {/* Stick on top of globe */}
    <line x1="60" y1="30" x2="60" y2="22" stroke="#8FA56C" strokeWidth="2" />
    <circle cx="60" cy="20" r="2.5" fill="#FFE89A" />
  </svg>
);

// 📖 Reading — tower made of stacked giant books
const ReadingTower = () => (
  <svg viewBox="0 0 120 210" className="tower-svg">
    <TowerShadow />
    {/* Books stacked, each slightly off-centre for charm */}
    {/* Bottom book — biggest */}
    <g transform="translate(60 175)">
      <rect x="-44" y="-14" width="88" height="22" rx="2" fill="#D9D0EB" />
      <rect x="-44" y="-14" width="88" height="4" fill="#9484BD" />
      <line x1="-40" y1="-14" x2="-40" y2="8" stroke="#9484BD" strokeWidth="2" />
      <rect x="-30" y="-6" width="22" height="2" fill="#FDFBF7" opacity="0.7" />
      <rect x="0" y="-6" width="30" height="2" fill="#FDFBF7" opacity="0.7" />
    </g>
    {/* Middle book */}
    <g transform="translate(58 144)">
      <rect x="-38" y="-13" width="76" height="20" rx="2" fill="#FCEAEA" />
      <rect x="-38" y="-13" width="76" height="4" fill="#E8A8A8" />
      <line x1="34" y1="-13" x2="34" y2="7" stroke="#E8A8A8" strokeWidth="2" />
      <rect x="-26" y="-5" width="22" height="2" fill="#9C5454" opacity="0.5" />
    </g>
    {/* Upper book */}
    <g transform="translate(62 116)">
      <rect x="-32" y="-12" width="64" height="18" rx="2" fill="#C9E8D6" />
      <rect x="-32" y="-12" width="64" height="4" fill="#7AB099" />
      <line x1="-28" y1="-12" x2="-28" y2="6" stroke="#7AB099" strokeWidth="2" />
      <rect x="-20" y="-4" width="20" height="2" fill="#3F6B55" opacity="0.5" />
    </g>
    {/* Top book — smallest, slightly tilted */}
    <g transform="translate(60 90) rotate(-4)">
      <rect x="-26" y="-11" width="52" height="16" rx="2" fill="#FFE89A" />
      <rect x="-26" y="-11" width="52" height="4" fill="#D9A93D" />
      <line x1="22" y1="-11" x2="22" y2="5" stroke="#D9A93D" strokeWidth="2" />
      <rect x="-18" y="-3" width="20" height="2" fill="#9C7427" opacity="0.5" />
    </g>
    {/* Bookmark/ribbon hanging from top book */}
    <path d="M62 78 L62 56 L66 60 L70 56 L70 78 Z" fill="#E8A8A8" />
    {/* Cosy reading lamp on very top */}
    <circle cx="62" cy="44" r="6" fill="#FFE89A" opacity="0.8" />
    <line x1="62" y1="50" x2="62" y2="58" stroke="#9C7427" strokeWidth="1.5" />
  </svg>
);

// 🏛️ History — clock tower with conical roof + Roman numerals (warm taupe)
const HistoryTower = () => (
  <svg viewBox="0 0 120 210" className="tower-svg">
    <TowerShadow />
    {/* Body */}
    <rect x="22" y="60" width="76" height="138" rx="5" fill="#E8DBC2" />
    {/* Left shadow side */}
    <rect x="22" y="60" width="13" height="138" rx="5" fill="#C9B58E" opacity="0.55" />
    {/* Stone band */}
    <rect x="18" y="54" width="84" height="10" rx="2" fill="#C9B58E" />
    {/* Conical pointed roof */}
    <path d="M22 54 L60 14 L98 54 Z" fill="#D89A7A" />
    <path d="M22 54 L60 14 L60 54 Z" fill="#A8745A" opacity="0.45" />
    {/* Tiny flag on the spire */}
    <line x1="60" y1="14" x2="60" y2="2" stroke="#6B5840" strokeWidth="1.5" />
    <path d="M60 2 L72 6 L60 10 Z" fill="#9484BD" />
    {/* Clock face */}
    <circle cx="60" cy="100" r="22" fill="#FDFBF7" stroke="#A8856B" strokeWidth="2.5" />
    <circle cx="60" cy="100" r="1.5" fill="#6B5840" />
    {/* Roman numerals on clock — XII, III, VI, IX */}
    <text x="60" y="86" textAnchor="middle" fontSize="6" fontWeight="700" fill="#6B5840">XII</text>
    <text x="78" y="103" textAnchor="middle" fontSize="6" fontWeight="700" fill="#6B5840">III</text>
    <text x="60" y="120" textAnchor="middle" fontSize="6" fontWeight="700" fill="#6B5840">VI</text>
    <text x="42" y="103" textAnchor="middle" fontSize="6" fontWeight="700" fill="#6B5840">IX</text>
    {/* Clock hands */}
    <line x1="60" y1="100" x2="60" y2="86" stroke="#6B5840" strokeWidth="1.8" strokeLinecap="round" />
    <line x1="60" y1="100" x2="71" y2="100" stroke="#6B5840" strokeWidth="1.5" strokeLinecap="round" />
    {/* Window below clock */}
    <rect x="52" y="138" width="16" height="20" rx="2" fill="#FDFBF7" opacity="0.85" />
    <line x1="60" y1="138" x2="60" y2="158" stroke="#A8856B" strokeWidth="1" />
    <line x1="52" y1="148" x2="68" y2="148" stroke="#A8856B" strokeWidth="1" />
    {/* Door */}
    <path d="M50 198 V172 a10 10 0 0 1 20 0 V198 Z" fill="#6B4A30" />
    <circle cx="60" cy="186" r="1.5" fill="#FFE89A" />
  </svg>
);

// ============================================================================
// 🆕 ISOMETRIC SAMPLE — what the full Minecraft-style version would look like
// ============================================================================
// Pure SVG isometric (30° angle, no 3D libs, no rotation). Same tower (History)
// rebuilt as a chunky cube with 6 visible floor bands, clock face, door,
// pyramid roof. Drawn so Louise can compare flat vs iso before we commit
// to redrawing all 7 towers.
//
// Geometry: standard game iso. Front corner closest to viewer; left & right
// faces fan back at 30° up. Top face is a rhombus. Heights are pure vertical.

const IsoHistoryTower = () => {
  const ox = 140;       // origin X (front-bottom corner)
  const oy = 320;       // origin Y
  const w = 90;         // body width along left-axis
  const d = 90;         // body depth along right-axis
  const h = 168;        // body height (6 floors × 28)
  const c = 0.866;      // cos(30°)
  const s = 0.5;        // sin(30°)

  // 8 corners
  const FB = [ox, oy];
  const LB = [ox - w * c, oy - w * s];
  const RB = [ox + d * c, oy - d * s];
  const FT = [ox, oy - h];
  const LT = [ox - w * c, oy - w * s - h];
  const RT = [ox + d * c, oy - d * s - h];
  const BT = [ox - w * c + d * c, oy - w * s - d * s - h];

  const apex = [ox, BT[1] - 60]; // pyramid roof apex
  const fh = h / 6; // floor height — 6 visible "floors"

  return (
    <svg viewBox="0 0 280 380" className="tower-svg">
      {/* Ground shadow */}
      <ellipse cx={ox} cy={oy + 10} rx="105" ry="14" fill="#000" opacity="0.18" />

      {/* === LEFT FACE — alternating floor bands (shaded side) === */}
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const z0 = i * fh;
        const z1 = (i + 1) * fh;
        const lighter = i % 2 === 0;
        return (
          <polygon
            key={`L${i}`}
            points={`${LB[0]},${LB[1] - z0} ${LB[0]},${LB[1] - z1} ${FB[0]},${FB[1] - z1} ${FB[0]},${FB[1] - z0}`}
            fill={lighter ? "#C9B58E" : "#B89A6E"}
          />
        );
      })}

      {/* === RIGHT FACE — alternating floor bands (lit side) === */}
      {[0, 1, 2, 3, 4, 5].map((i) => {
        const z0 = i * fh;
        const z1 = (i + 1) * fh;
        const lighter = i % 2 === 0;
        return (
          <polygon
            key={`R${i}`}
            points={`${FB[0]},${FB[1] - z0} ${FB[0]},${FB[1] - z1} ${RB[0]},${RB[1] - z1} ${RB[0]},${RB[1] - z0}`}
            fill={lighter ? "#F0E5CE" : "#E2D1B0"}
          />
        );
      })}

      {/* Floor divider lines wrapping around the front corner */}
      {[1, 2, 3, 4, 5].map((i) => {
        const z = i * fh;
        return (
          <g key={`div${i}`} stroke="#7A5C3A" strokeWidth="0.8" opacity="0.45">
            <line x1={LB[0]} y1={LB[1] - z} x2={FB[0]} y2={FB[1] - z} />
            <line x1={FB[0]} y1={FB[1] - z} x2={RB[0]} y2={RB[1] - z} />
          </g>
        );
      })}
      {/* Front vertical edge */}
      <line x1={FB[0]} y1={FB[1]} x2={FT[0]} y2={FT[1]} stroke="#7A5C3A" strokeWidth="1.2" opacity="0.5" />

      {/* === DOOR on left face, ground floor === */}
      {(() => {
        const tStart = 0.45, tEnd = 0.85;
        const x0 = LB[0] + (FB[0] - LB[0]) * tStart;
        const y0 = LB[1] + (FB[1] - LB[1]) * tStart;
        const x1 = LB[0] + (FB[0] - LB[0]) * tEnd;
        const y1 = LB[1] + (FB[1] - LB[1]) * tEnd;
        const dh = fh * 0.82;
        return (
          <g>
            <polygon
              points={`${x0},${y0} ${x0},${y0 - dh} ${x1},${y1 - dh} ${x1},${y1}`}
              fill="#5C4128"
            />
            <ellipse
              cx={(x0 + x1) / 2}
              cy={(y0 + y1) / 2 - dh}
              rx={(x1 - x0) / 2}
              ry={3}
              fill="#5C4128"
            />
            <circle cx={x0 + (x1 - x0) * 0.72} cy={y0 + (y1 - y0) * 0.72 - dh * 0.5} r="1.4" fill="#FFE89A" />
          </g>
        );
      })()}

      {/* === CLOCK FACE on right face, floor 4 === */}
      {(() => {
        const z = 3.5 * fh;
        const cx = FB[0] + (RB[0] - FB[0]) * 0.5;
        const cy = FB[1] + (RB[1] - FB[1]) * 0.5 - z;
        return (
          <g transform={`rotate(-15 ${cx} ${cy})`}>
            <ellipse cx={cx} cy={cy} rx="13" ry="10.5" fill="#FDFBF7" stroke="#7A5C3A" strokeWidth="1.6" />
            <text x={cx} y={cy - 4.5} textAnchor="middle" fontSize="4" fontWeight="700" fill="#5C4128">XII</text>
            <text x={cx + 8.5} y={cy + 1.2} textAnchor="middle" fontSize="4" fontWeight="700" fill="#5C4128">III</text>
            <text x={cx} y={cy + 7} textAnchor="middle" fontSize="4" fontWeight="700" fill="#5C4128">VI</text>
            <text x={cx - 8.5} y={cy + 1.2} textAnchor="middle" fontSize="4" fontWeight="700" fill="#5C4128">IX</text>
            <line x1={cx} y1={cy} x2={cx} y2={cy - 5.5} stroke="#5C4128" strokeWidth="1.2" strokeLinecap="round" />
            <line x1={cx} y1={cy} x2={cx + 6} y2={cy} stroke="#5C4128" strokeWidth="1" strokeLinecap="round" />
            <circle cx={cx} cy={cy} r="0.9" fill="#5C4128" />
          </g>
        );
      })()}

      {/* === SMALL WINDOWS on right face on the floors WITHOUT the clock === */}
      {[0, 1, 2, 4, 5].map((i) => {
        const zMid = (i + 0.5) * fh;
        const cx = FB[0] + (RB[0] - FB[0]) * 0.5;
        const cy = FB[1] + (RB[1] - FB[1]) * 0.5 - zMid;
        return (
          <g key={`w${i}`} transform={`rotate(-15 ${cx} ${cy})`}>
            <rect x={cx - 4} y={cy - 6} width="8" height="11" rx="1.2" fill="#FDFBF7" opacity="0.88" />
            <line x1={cx} y1={cy - 6} x2={cx} y2={cy + 5} stroke="#7A5C3A" strokeWidth="0.5" />
            <line x1={cx - 4} y1={cy} x2={cx + 4} y2={cy} stroke="#7A5C3A" strokeWidth="0.5" />
          </g>
        );
      })}

      {/* === TINY WINDOWS on left face (one per floor, for symmetry) === */}
      {[0, 1, 3, 4, 5].map((i) => {
        // skip floor 0 (door is there) and floor 2 to keep it interesting
        const zMid = (i + 0.5) * fh;
        const cx = LB[0] + (FB[0] - LB[0]) * 0.25;
        const cy = LB[1] + (FB[1] - LB[1]) * 0.25 - zMid;
        return (
          <g key={`lw${i}`} transform={`rotate(15 ${cx} ${cy})`}>
            <rect x={cx - 3} y={cy - 5} width="6" height="9" rx="1" fill="#FFE89A" opacity="0.8" />
          </g>
        );
      })}

      {/* === TOP FACE (mostly hidden under roof, peeks at edges) === */}
      <polygon
        points={`${LT[0]},${LT[1]} ${BT[0]},${BT[1]} ${RT[0]},${RT[1]} ${FT[0]},${FT[1]}`}
        fill="#DCCBA8"
      />

      {/* === ROOF — 4-sided pyramid; only 2 faces visible === */}
      <polygon
        points={`${FT[0]},${FT[1]} ${LT[0]},${LT[1]} ${apex[0]},${apex[1]}`}
        fill="#A8745A"
      />
      <polygon
        points={`${FT[0]},${FT[1]} ${RT[0]},${RT[1]} ${apex[0]},${apex[1]}`}
        fill="#D89A7A"
      />

      {/* Roof rim (battlement) — small lip where roof meets walls */}
      <polygon
        points={`${LT[0]},${LT[1]} ${FT[0]},${FT[1]} ${RT[0]},${RT[1]}`}
        fill="none"
        stroke="#7A5C3A"
        strokeWidth="0.8"
        opacity="0.5"
      />

      {/* Flag pole + flag on apex */}
      <line x1={apex[0]} y1={apex[1]} x2={apex[0]} y2={apex[1] - 16} stroke="#5C4128" strokeWidth="1.4" />
      <path d={`M ${apex[0]} ${apex[1] - 16} L ${apex[0] + 14} ${apex[1] - 12} L ${apex[0]} ${apex[1] - 8} Z`} fill="#9484BD" />
    </svg>
  );
};

// ---------- Tower descriptor ----------
// 7 towers — 3 in back row (peeking between front gaps), 4 in front row.
// (x,y) are pixel offsets from the cluster centre. y is "lift off ground"
// (positive = raised). z is paint order (front towers cover back towers).
const TOWERS = [
  // Back row — flanking + centre. Science gets EXTRA lift so its flask
  // peeks well above the front towers and stays clickable.
  { key: "english",   label: "English",   emoji: "📚", Comp: EnglishTower,   greeting: "Welcome to the English tower! Shall we read, write a story, or explore words together?",                               x: -260, y: 100, scale: 0.95, z: 1 },
  { key: "science",   label: "Science",   emoji: "🔬", Comp: ScienceTower,   greeting: "Welcome to the Science tower! What shall we discover today — animals, space, the body, or something else?",         x: 0,    y: 170, scale: 1.0,  z: 1 },
  { key: "geography", label: "Geography", emoji: "🌍", Comp: GeographyTower, greeting: "Welcome to the Geography tower! Where in the world shall we visit today?",                                            x: 260,  y: 100, scale: 0.95, z: 1 },
  // Front row — main 4 towers, bigger, on the ground
  { key: "maths",     label: "Maths",     emoji: "🔢", Comp: MathsTower,     greeting: "Welcome to the Maths tower! What shall we figure out — numbers, shapes, or a tricky puzzle?",                        x: -195, y: 0,  scale: 1.15, z: 2 },
  { key: "history",   label: "History",   emoji: "🏛️", Comp: HistoryTower,   greeting: "Welcome to the History tower! Which time do you want to travel to — ancient kings, world wars, or something else?", x: -65,  y: 0,  scale: 1.18, z: 2 },
  { key: "art",       label: "Art",       emoji: "🎨", Comp: ArtTower,       greeting: "Welcome to the Art tower! Shall we draw, mix colours, or talk about a famous painting?",                              x: 65,   y: 0,  scale: 1.15, z: 2 },
  { key: "reading",   label: "Reading",   emoji: "📖", Comp: ReadingTower,   greeting: "Welcome to the Reading tower! Shall I read you a tiny story, or help you with a book?",                              x: 195,  y: 0,  scale: 1.15, z: 2 },
];

export default function LearningTowersPreview() {
  const [hovered, setHovered] = useState(null);
  const [picked, setPicked] = useState(null);

  return (
    <div
      className="min-h-screen w-full overflow-hidden relative"
      style={{
        background:
          "radial-gradient(ellipse at 50% 30%, #FFF7D6 0%, #FDFBF7 45%, #F3F0E6 100%)",
      }}
      data-testid="learning-towers-preview"
    >
      {/* Soft drifting clouds */}
      <div className="absolute top-12 left-10 w-32 h-12 bg-white/60 rounded-full blur-xl" />
      <div className="absolute top-24 right-20 w-40 h-14 bg-white/50 rounded-full blur-2xl" />
      <div className="absolute top-40 left-1/3 w-28 h-10 bg-white/40 rounded-full blur-xl" />

      {/* Header */}
      <div className="relative z-10 pt-10 pb-2 text-center">
        <div className="text-xs uppercase tracking-[0.3em] text-stone-400 mb-2">
          Visual prototype · not wired to chat yet
        </div>
        <h1 className="text-4xl sm:text-5xl font-bold text-stone-700">
          🏰 Learning Towers
        </h1>
        <p className="text-sm sm:text-base text-stone-500 mt-2 max-w-md mx-auto px-4">
          Hover or tap a tower to see how a kid would pick a subject.
          Click one to preview the welcome message.
        </p>
      </div>

      {/* Tower cluster — absolute positioning gives true village-skyline depth */}
      <div className="relative z-10 mt-6 flex items-center justify-center w-full px-4">
        <div className="relative" style={{ width: "780px", height: "360px", maxWidth: "100%" }}>
          {/* Soft "ground island" — the platform towers stand on */}
          <div
            className="absolute left-1/2 -translate-x-1/2 rounded-full"
            style={{
              bottom: "-6px",
              width: "92%",
              height: "44px",
              background:
                "radial-gradient(ellipse at center, rgba(120,90,40,0.18) 0%, rgba(120,90,40,0) 70%)",
            }}
          />

          {TOWERS.map((t) => (
            <div
              key={t.key}
              className="absolute bottom-0 left-1/2"
              style={{
                transform: `translateX(calc(-50% + ${t.x}px)) translateY(-${t.y}px)`,
                zIndex: t.z,
                width: `${110 * t.scale}px`,
                height: `${210 * t.scale}px`,
              }}
            >
              <TowerCard
                tower={t}
                scale={t.scale}
                hovered={hovered === t.key}
                onHover={() => setHovered(t.key)}
                onLeave={() => setHovered((h) => (h === t.key ? null : h))}
                onPick={() => setPicked(t)}
              />
            </div>
          ))}
        </div>
      </div>

      {/* Pick preview modal */}
      {picked && (
        <div
          className="fixed inset-0 z-50 bg-black/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={(e) => { if (e.target === e.currentTarget) setPicked(null); }}
        >
          <div className="bg-[#FDFBF7] border border-stone-200 rounded-3xl max-w-md w-full p-6 shadow-2xl" data-testid="tower-preview-modal">
            <div className="flex items-start gap-4">
              <div className="text-5xl shrink-0">{picked.emoji}</div>
              <div className="flex-1">
                <div className="text-xs uppercase tracking-widest text-stone-400">
                  Preview greeting
                </div>
                <div className="text-lg font-semibold text-stone-700 mt-0.5">
                  Poodle says…
                </div>
                <div className="text-stone-600 mt-2 leading-relaxed">
                  "{picked.greeting}"
                </div>
              </div>
            </div>
            <div className="mt-5 pt-4 border-t border-stone-200 flex items-center justify-between">
              <div className="text-xs text-stone-400">
                In the real build, the chat input would now be tuned for{" "}
                <strong className="text-stone-600">{picked.label}</strong>.
              </div>
              <button
                onClick={() => setPicked(null)}
                className="text-sm px-4 py-1.5 bg-stone-700 text-white rounded-full hover:bg-stone-600"
                data-testid="tower-preview-close"
              >
                Got it
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Footer note */}
      <div className="relative z-10 mt-8 pb-10 text-center text-xs text-stone-400 px-4">
        Style: soft pastel SVG, no 3D libs, no extra dependencies. Mobile-friendly.
      </div>

      {/* Local CSS for animations */}
      <style>{`
        .tower-svg {
          width: 100%;
          height: 100%;
          display: block;
          overflow: visible;
        }
        @keyframes tower-bob {
          0%, 100% { transform: translateY(0); }
          50% { transform: translateY(-6px); }
        }
        .tower-anim-1 { animation: tower-bob 3.4s ease-in-out infinite; }
        .tower-anim-2 { animation: tower-bob 3.8s ease-in-out infinite 0.4s; }
        .tower-anim-3 { animation: tower-bob 4.1s ease-in-out infinite 0.8s; }
      `}</style>
    </div>
  );
}

function TowerCard({ tower, scale, hovered, onHover, onLeave, onPick }) {
  // Pick a bob speed per tower so they're not all in sync
  const animClass = `tower-anim-${(tower.key.charCodeAt(0) % 3) + 1}`;
  return (
    <div
      className="relative w-full h-full flex flex-col items-center cursor-pointer select-none"
      onMouseEnter={onHover}
      onMouseLeave={onLeave}
      onClick={() => onPick(tower)}
      data-testid={`tower-${tower.key}`}
    >
      {/* Hover label tooltip */}
      <div
        className={`absolute -top-9 left-1/2 -translate-x-1/2 px-3 py-1 rounded-full text-xs font-medium whitespace-nowrap transition-all duration-200 pointer-events-none shadow-md z-10 ${
          hovered
            ? "opacity-100 translate-y-0 bg-stone-700 text-white"
            : "opacity-0 -translate-y-1 bg-stone-700 text-white"
        }`}
      >
        {tower.emoji} {tower.label}
      </div>

      {/* The tower itself */}
      <div
        className={`${animClass} transition-transform duration-300 w-full h-full ${
          hovered ? "scale-110 drop-shadow-[0_8px_16px_rgba(0,0,0,0.18)]" : ""
        }`}
        style={{ transformOrigin: "bottom center" }}
      >
        <tower.Comp />
      </div>
    </div>
  );
}
